"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import OnboardingTour from "./onboarding/OnboardingTour";
import OnboardingSlideshow from "./onboarding/OnboardingSlideshow";

const ONBOARDING_MODAL_VISIBILITY_EVENT = "flare:onboarding-modal-visibility";

export default function OnboardingGate() {
  const pathname = usePathname();
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  // null = unknown (still loading) | false = show slideshow | true = slideshow done
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);
  // Preference values seeded into the slideshow so per-step saves can be resumed.
  const [slideshowPrefs, setSlideshowPrefs] = useState({
    defaultPlayerColor: null,
    referralSource: null,
    referralSourceOther: null,
  });
  const hasCompletedOnboardingRef = useRef(false);
  const isCompletingOnboardingRef = useRef(false);
  const [profileReloadKey, setProfileReloadKey] = useState(0);

  // Only run onboarding logic on internal authenticated app routes.
  // This avoids making background /api/profile calls on public pages like /video/[id].
  const shouldEnableOnboardingForCurrentRoute = (() => {
    try {
      const currentPathname = typeof pathname === "string" ? pathname : "";

      // Embeds run inside iframes and should not load auth/onboarding UX (and should not call /api/profile).
      if (currentPathname === "/embed" || currentPathname.startsWith("/embed/")) return false;

      // Public viewer pages should not try to load onboarding at all.
      if (currentPathname === "/video" || currentPathname.startsWith("/video/")) return false;
      if (currentPathname === "/virtual-video" || currentPathname.startsWith("/virtual-video/")) return false;

      // Onboarding tour is library-centric: every spotlight step targets a
      // [data-tour="..."] element that only exists in /library components.
      // Enabling it elsewhere leaves the user stuck on a dark fallback overlay.
      if (currentPathname === "/library" || currentPathname.startsWith("/library/")) return true;

      return false;
    } catch {
      return false;
    }
  })();

  const shouldDisableOnboardingForCurrentRoute = !shouldEnableOnboardingForCurrentRoute;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const isSlideshowOpen = onboardingCompleted === false;
    const isOnboardingOpen = Boolean(
      (shouldShowOnboarding || isSlideshowOpen) &&
        !shouldDisableOnboardingForCurrentRoute,
    );

    try {
      if (isOnboardingOpen) {
        document.body.dataset.flareOnboardingModalOpen = "true";
      } else {
        delete document.body.dataset.flareOnboardingModalOpen;
      }
    } catch {
      // ignore DOM access issues
    }

    try {
      window.dispatchEvent(
        new CustomEvent(ONBOARDING_MODAL_VISIBILITY_EVENT, {
          detail: { isOpen: isOnboardingOpen },
        }),
      );
    } catch {
      // ignore event dispatch issues
    }

    return () => {
      try {
        delete document.body.dataset.flareOnboardingModalOpen;
      } catch {
        // ignore
      }
      try {
        window.dispatchEvent(
          new CustomEvent(ONBOARDING_MODAL_VISIBILITY_EVENT, {
            detail: { isOpen: false },
          }),
        );
      } catch {
        // ignore
      }
    };
  }, [shouldShowOnboarding, onboardingCompleted, shouldDisableOnboardingForCurrentRoute]);

  useEffect(() => {
    if (shouldDisableOnboardingForCurrentRoute) {
      setShouldShowOnboarding(false);
      return undefined;
    }
    if (hasCompletedOnboardingRef.current) {
      setShouldShowOnboarding(false);
      return undefined;
    }

    let isCancelled = false;

    const loadProfile = async () => {
      try {
        const response = await fetch("/api/profile", { cache: "no-store" });
        if (!response.ok) {
          if (!isCancelled) {
            setShouldShowOnboarding(false);
          }
          return;
        }

        const profileData = await response.json();
        const user = profileData?.user;
        if (!user) return;

        if (isCancelled) return;

        // Seed the slideshow with any preferences already saved (so a refresh
        // mid-onboarding keeps the user's color / attribution answers).
        setSlideshowPrefs({
          defaultPlayerColor: user.defaultPlayerColor ?? null,
          referralSource: user.referralSource ?? null,
          referralSourceOther: user.referralSourceOther ?? null,
        });

        // Slideshow gates on onboardingCompleted; the tour gates on isFirstLogin.
        setOnboardingCompleted(user.onboardingCompleted === true);

        if (user.isFirstLogin === true) {
          setShouldShowOnboarding(true);
          return;
        }

        setShouldShowOnboarding(false);
      } catch {
        // Ignore profile fetch failures; onboarding is a best-effort UI.
      }
    };

    void loadProfile();

    return () => {
      isCancelled = true;
    };
  }, [pathname, shouldDisableOnboardingForCurrentRoute, profileReloadKey]);

  const markOnboardingComplete = async (useCasePreference = null) => {
    if (hasCompletedOnboardingRef.current) return;
    if (isCompletingOnboardingRef.current) return;

    isCompletingOnboardingRef.current = true;

    // Optimistic UX: close immediately.
    setShouldShowOnboarding(false);

    try {
      const response = await fetch("/api/onboarding/complete", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ useCasePreference }),
      });

      if (!response.ok) {
        throw new Error(`Failed to complete onboarding (${response.status})`);
      }

      hasCompletedOnboardingRef.current = true;
    } catch (unknownThrownValue) {
      // If the request failed, reopen so the user can try again.
      setShouldShowOnboarding(true);
      // This is a UX flow; don't throw here (it can become an unhandled rejection in the app).
      try {
        const errorMessage =
          unknownThrownValue instanceof Error
            ? unknownThrownValue.message
            : String(unknownThrownValue);
        console.error("[OnboardingGate] Failed to complete onboarding:", errorMessage);
      } catch {
        // ignore logging failures
      }
    } finally {
      isCompletingOnboardingRef.current = false;
    }
  };

  // Called by the slideshow when it PATCHes onboardingCompleted = true.
  // Optimistically flips the local flag (so the slideshow unmounts and the tour
  // can take over), then refetches the profile to confirm.
  const refetchProfile = useCallback(() => {
    setOnboardingCompleted(true);
    setProfileReloadKey((key) => key + 1);
  }, []);

  if (shouldDisableOnboardingForCurrentRoute) {
    return null;
  }

  // Slideshow runs first, before the guided tour. The slideshow's completion
  // PATCH flips onboardingCompleted -> true; because isFirstLogin is still true,
  // the tour then begins. Clean handoff.
  if (onboardingCompleted === false) {
    return (
      <OnboardingSlideshow
        onComplete={refetchProfile}
        initialBrandColor={slideshowPrefs.defaultPlayerColor}
        initialReferralSource={slideshowPrefs.referralSource}
        initialReferralSourceOther={slideshowPrefs.referralSourceOther}
      />
    );
  }

  return (
    <OnboardingTour
      isOpen={shouldShowOnboarding}
      markOnboardingComplete={markOnboardingComplete}
    />
  );
}




